import Navbar from "../components/Navbar";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Ranking.css";
import Buttons from "../components/Buttons";
import { GameScoreService } from "../lib/game-score-service";
import { useAuth } from "../contexts/AuthContext";
import { GameScore } from "../lib/supabase";

interface UserRanking {
  position: number;
  score: number;
  email: string;
}

export default function Ranking() {
  const [rankingData, setRankingData] = useState<GameScore[]>([]);
  const [userRanking, setUserRanking] = useState<UserRanking | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const loadData = async () => {
      await loadRanking();
      if (user) {
        await loadUserRanking();
      }
    };
    loadData();
  }, [user]);

  const loadRanking = async () => {
    try {
      setLoading(true);
      setError(null);

      const scores = await GameScoreService.getBestScoresLeaderboard(50);

      if (scores) {
        setRankingData(scores);
      } else {
        setError("Nessun punteggio disponibile");
      }
    } catch (err: any) {
      console.error("Errore nel caricamento della classifica:", err);
      setError("Errore nel caricamento della classifica");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      setError(null);

      await loadRanking();

      if (user) {
        await loadUserRanking();
      }
    } catch (err: any) {
      console.error("Errore nell'aggiornamento della classifica:", err);
      setError("Errore nell'aggiornamento della classifica");
    } finally {
      setRefreshing(false);
    }
  };

  const loadUserRanking = async () => {
    if (!user) return;

    try {
      const ranking = await GameScoreService.getUserRankingPosition(user.id);
      setUserRanking(ranking);

      if (ranking) {
        const userScore = ranking.score.toString();
        localStorage.setItem("ranking", userScore);
        sessionStorage.setItem("ranking", userScore);
      }
    } catch (err: any) {
      console.error("Errore nel caricamento del punteggio utente:", err);
    }
  };

  const handleMiglioraPunteggio = () => {
    navigate("/select-champion");
  };

  const getNickname = (email: string) => {
    return email.split("@")[0];
  };

  return (
    <div className="ranking-container">
      <Navbar update={true} onRefresh={handleRefresh} />

      {refreshing && (
        <div className="refresh-overlay">
          <div className="spinner"></div>
          <p>Aggiornamento classifica...</p>
        </div>
      )}

      <div className="ranking-grid">
        <div className="grid-head">
          <div className="title">
            <h2>Classifica</h2>
          </div>

          {loading ? (
            <div className="loading-medals">
              <p>Caricamento classifica...</p>
            </div>
          ) : rankingData.length >= 3 ? (
            <div className="medal-rank">
              <img
                src="/assets/ranking/coriandolisinistra.svg"
                alt="Coriandoli sinistra"
                className="confetti confetti-left"
                width={100}
                height={100}
              />
              <img
                src="/assets/ranking/coriandolidestra.svg"
                alt="Coriandoli destra"
                className="confetti confetti-right"
                width={100}
                height={100}
              />

              <div className="silver medal">
                <img
                  src="/assets/ranking/silver-medal.svg"
                  alt="Silver Medal"
                  width={80}
                  height={80}
                />
                <p
                  className="medal-nickname"
                  style={{ fontSize: "0.7rem", color: "#5062CD" }}
                >
                  {getNickname(rankingData[1].email)}
                </p>
                <p
                  className="score silver-score"
                  style={{ color: "#849FBF", fontSize: "2.2rem" }}
                >
                  {rankingData[1].score}
                </p>
              </div>

              <div className="golden medal">
                <img
                  src="/assets/ranking/gold-medal.svg"
                  alt="Gold Medal"
                  width={100}
                  height={100}
                />
                <svg
                  className="gold-circle"
                  width="65"
                  height="65"
                  viewBox="0 0 65 65"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="32.1559"
                    cy="32.1559"
                    r="32.1559"
                    fill="#FEEA66"
                  />
                </svg>
                <p
                  className="medal-nickname"
                  style={{ fontSize: "0.7rem", color: "#5062CD" }}
                >
                  {getNickname(rankingData[0].email)}
                </p>
                <p
                  className="score gold-score"
                  style={{ color: "#FF9600", fontSize: "3rem" }}
                >
                  {rankingData[0].score}
                </p>
              </div>

              <div className="bronze medal">
                <img
                  src="/assets/ranking/bronze-medal.svg"
                  alt="Bronze Medal"
                  width={80}
                  height={80}
                />
                <p
                  className="medal-nickname"
                  style={{ fontSize: "0.7rem", color: "#5062CD" }}
                >
                  {getNickname(rankingData[2].email)}
                </p>
                <p
                  className="score bronze-score"
                  style={{ color: "#CD7900", fontSize: "2.2rem" }}
                >
                  {rankingData[2].score}
                </p>
              </div>
            </div>
          ) : null}
        </div>

        {userRanking && (
          <>
            <div className="user-score-section">
              <h3 className="user-score-title">Il Tuo Punteggio</h3>
              <div className="user-score-card">
                <div className="rank-info">
                  <div
                    className={`rank-position ${
                      userRanking.position === 1
                        ? "gold"
                        : userRanking.position === 2
                        ? "silver"
                        : userRanking.position === 3
                        ? "bronze"
                        : "other"
                    }`}
                  >
                    {userRanking.position <= 3 && (
                      <img
                        src={`/assets/ranking/${
                          userRanking.position === 1
                            ? "gold"
                            : userRanking.position === 2
                            ? "silver"
                            : "bronze"
                        }-medal.svg`}
                        alt={`${
                          userRanking.position === 1
                            ? "Gold"
                            : userRanking.position === 2
                            ? "Silver"
                            : "Bronze"
                        } Medal`}
                        width={60}
                        height={60}
                        className="list-medal-icon"
                      />
                    )}
                    {userRanking.position === 1 && (
                      <svg
                        className="list-gold-circle"
                        width="65"
                        height="65"
                        viewBox="0 0 65 65"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle
                          cx="32.1559"
                          cy="32.1559"
                          r="32.1559"
                          fill="#FEEA66"
                        />
                      </svg>
                    )}
                    <span className="rank-number">{userRanking.position}</span>
                  </div>
                  <p className="rank-nickname">
                    {getNickname(userRanking.email)}
                  </p>
                  <p className="rank-score">{userRanking.score}</p>
                </div>
              </div>
            </div>

            <div className="user-action-button">
              <Buttons
                oneButton={true}
                onClickFirst={handleMiglioraPunteggio}
                textFirst="MIGLIORA IL TUO PUNTEGGIO"
              />
            </div>
          </>
        )}

        <div className="body">
          <div className="rank-list">
            {loading ? (
              <div className="loading-container">
                <p>Caricamento...</p>
              </div>
            ) : error ? (
              <div className="error-container">
                <p>{error}</p>
                <button onClick={loadRanking}>Riprova</button>
              </div>
            ) : rankingData.length === 0 ? (
              <div className="empty-container">
                <p>Nessun punteggio disponibile</p>
              </div>
            ) : (
              <div className="position-list">
                {rankingData.map((item, index) => (
                  <div key={item.id} className="position">
                    <div className="rank-info">
                      <div
                        className={`rank-position ${
                          index + 1 === 1
                            ? "gold"
                            : index + 1 === 2
                            ? "silver"
                            : index + 1 === 3
                            ? "bronze"
                            : "other"
                        }`}
                      >
                        {index + 1 <= 3 && (
                          <img
                            src={`/assets/ranking/${
                              index + 1 === 1
                                ? "gold"
                                : index + 1 === 2
                                ? "silver"
                                : "bronze"
                            }-medal.svg`}
                            alt={`${
                              index + 1 === 1
                                ? "Gold"
                                : index + 1 === 2
                                ? "Silver"
                                : "Bronze"
                            } Medal`}
                            width={60}
                            height={60}
                            className="list-medal-icon"
                          />
                        )}
                        {index + 1 === 1 && (
                          <svg
                            className="list-gold-circle"
                            width="65"
                            height="65"
                            viewBox="0 0 65 65"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <circle
                              cx="32.1559"
                              cy="32.1559"
                              r="32.1559"
                              fill="#FEEA66"
                            />
                          </svg>
                        )}
                        <span className="rank-number">{index + 1}</span>
                      </div>
                      <p className="rank-nickname">{getNickname(item.email)}</p>
                      <p className="rank-score">{item.score}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
